<script>
import { mapGetters, mapActions } from 'vuex';
import {
  iIcon,
  iKeyIcon,
  iKeyIconShape,
  iKeyIconSize,
  iKeyIconStyle,
  iKeyIconType,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iSpinner,
  iBadge,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import LoadingBoxes from '@/V2/components/LoadingBoxes.vue';
import {
  COMPLIANCE_RESULT,
  COMPLIANCE_RESULT_TO_READABLE_LABEL,
} from '@/constants/businessComplianceAgent';
import UpsellScreenCompliance from '@/V2/components/upsell/UpsellScreenCompliance.vue';
import CustomerComplianceReportItem from './CustomerComplianceReportItem.vue';
import { RUN_STATUS } from '@/constants/analyst-constants';
import {
  getBeneficialOwnerNames,
  getCanonicalProfileObj,
  getLatestBusinessComplianceAgentRun,
} from '@/V2/customers/business-compliance-agent/utils';
import { hasAgentRunInProgress } from '@/utils/ai-agents';

export default {
  components: {
    iBadge,
    iButton,
    iIcon,
    iKeyIcon,
    iSpinner,
    LoadingBoxes,
    UpsellScreenCompliance,
    CustomerComplianceReportItem,
  },
  data() {
    return {
      palette,
      COMPLIANCE_RESULT,
      COMPLIANCE_RESULT_TO_READABLE_LABEL,
      RUN_STATUS,
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iKeyIconShape,
      iKeyIconSize,
      iKeyIconStyle,
      iKeyIconType,
      isDataLoading: false,
    };
  },
  computed: {
    ...mapGetters([
      'getCachedCustomerById',
      'canViewBusinessComplianceAgent',
      'getCachedDocumentReviewAgentRunsForCustomerUUID',
      'getCachedBusinessComplianceAgentRunsForCustomerUUID',
      'someDocumentUploadingOrProcessing',
    ]),
    customerId() {
      return this.$route.params.customer_id;
    },
    customer() {
      return this.getCachedCustomerById(this.customerId);
    },
    anyDocumentReviewAgentIsRunning() {
      const runs = this.getCachedDocumentReviewAgentRunsForCustomerUUID(
        this.customerId,
      );
      return hasAgentRunInProgress(runs);
    },
    latestComplianceRun() {
      if (this.customer?.business_compliance_agent_v1) {
        return this.customer.business_compliance_agent_v1;
      } else {
        const runs = this.getCachedBusinessComplianceAgentRunsForCustomerUUID(
          this.customerId,
        );
        return getLatestBusinessComplianceAgentRun(runs);
      }
    },
    complianceStatus() {
      return this.someDocumentUploadingOrProcessing ||
        this.anyDocumentReviewAgentIsRunning
        ? RUN_STATUS.RUNNING
        : this.latestComplianceRun?.status;
    },
    showLoadingState() {
      return this.complianceIsRunning || this.isDataLoading;
    },
    complianceIsRunning() {
      return [RUN_STATUS.RUNNING, RUN_STATUS.PENDING].includes(
        this.latestComplianceRun?.status,
      );
    },
    complianceRunIsFinished() {
      return this.latestComplianceRun?.status === RUN_STATUS.FINISHED;
    },
    complianceRunFailed() {
      return this.latestComplianceRun?.status === RUN_STATUS.FAILED;
    },
    complianceRunNotStarted() {
      return !this.isDataLoading && !this.latestComplianceRun;
    },
    profileOverviewObj() {
      return getCanonicalProfileObj(
        this.latestComplianceRun?.output?.canonical_profile,
      );
    },
    allChecks() {
      return [
        ...(this.latestComplianceRun?.output?.company_checks ?? []),
        ...(this.latestComplianceRun?.output?.beneficial_owner_checks?.flatMap(
          (owner) => owner.checks,
        ) ?? []),
      ];
    },
    companyChecks() {
      return this.latestComplianceRun?.output?.company_checks ?? [];
    },
    beneficialOwnerNames() {
      return getBeneficialOwnerNames(this.latestComplianceRun);
    },
  },
  watch: {
    '$route.params.customer_id': {
      immediate: true,
      handler: function (newCustomerId, oldCustomerId) {
        if (newCustomerId !== oldCustomerId) {
          this.fetchInitialData();
        }
      },
    },
  },
  mounted() {
    this.fetchInitialData();
  },
  methods: {
    ...mapActions([
      'fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID',
      'fetchAndCacheDocumentReviewAgentRunsForCustomerUUID',
      'fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID',
    ]),
    async fetchInitialData() {
      this.isDataLoading = true;
      let customerId = this.$route.params.customer_id;
      const promises = [
        this.fetchAndCacheFirstPageOfSummaryDocumentsForCustomerUUID(
          customerId,
        ),
        this.fetchAndCacheDocumentReviewAgentRunsForCustomerUUID(customerId),
        this.fetchAndCacheBusinessComplianceAgentRunsForCustomerUUID(
          customerId,
        ),
      ];
      await Promise.allSettled(promises);
      this.isDataLoading = false;
    },

    formatFieldName(key) {
      return key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    getChecksForBeneficialOwner(fullName) {
      const allChecks =
        this.latestComplianceRun?.output?.beneficial_owner_checks ?? [];
      if (allChecks.length === 0) {
        return [];
      }
      const filtered = allChecks.filter(
        (c) => c.beneficial_owner_full_name === fullName,
      );
      return filtered.at(0)?.checks ?? [];
    },
    goBack() {
      this.$router.push({
        name: 'customer-compliance',
        params: { customer_id: this.customerId },
      });
    },
  },
};
</script>

<template>
  <div class="customer-compliance-report">
    <upsell-screen-compliance
      v-if="!canViewBusinessComplianceAgent && !isDataLoading"
    ></upsell-screen-compliance>
    <div v-else class="d-flex flex-column i-gap-11">
      <!-- back button-->
      <i-button
        data-testid="customers-page-back-button"
        left-icon="medium-arrows-left"
        :type="iButtonTypes.NEUTRAL"
        :button-style="iButtonStyles.SOLID_LIGHT"
        :size="iButtonSizes.SMALL"
        label="Back to Compliance"
        class="back-button"
        @button-click="goBack"
      />

      <div class="customer-compliance-container i-py-14 i-px-11">
        <!-- title -->
        <div class="d-flex flex-row align-items-center i-gap-6">
          <div class="title i-text-heading-3">Compliance Report</div>
          <i-spinner v-if="showLoadingState" size="sm"></i-spinner>
        </div>

        <!-- loading state -->
        <div v-if="showLoadingState" class="d-flex flex-column i-mt-11 i-gap-9">
          <loading-boxes
            :config="{
              lengths: [564, 529, 564, 529, 509],
              repeat: 1,
            }"
          ></loading-boxes>
          <loading-boxes
            :config="{
              lengths: [564, 529, 498, 529, 498],
              repeat: 1,
            }"
          ></loading-boxes>
          <loading-boxes
            :config="{
              lengths: [564, 529, 498, 529, 337],
              repeat: 1,
            }"
          ></loading-boxes>
        </div>

        <!-- not started state -->
        <div
          v-else-if="complianceRunNotStarted"
          class="empty-state d-flex flex-column align-items-center i-py-15 i-mt-11 i-radius-2xl i-gap-8"
        >
          <i-key-icon
            icon="medium-text-4"
            :shape="iKeyIconShape.SQUARE"
            :type="iKeyIconType.NEUTRAL"
            :icon-style="iKeyIconStyle.OUTLINE"
            :size="iKeyIconSize.MEDIUM"
          ></i-key-icon>
          <div class="text d-flex flex-column i-gap-1 align-items-center">
            <div class="title i-text-button-2 i-text-neutral-120">
              Compliance Report
            </div>
            <div class="subtitle i-text-body-2 i-text-neutral-110">
              Business Compliance Agent
            </div>
          </div>
        </div>

        <!-- failed state -->
        <div
          v-else-if="complianceRunFailed"
          class="empty-state d-flex flex-column align-items-center i-py-15 i-mt-11 i-radius-2xl i-gap-8"
        >
          <i-key-icon
            icon="medium-solid-alert-triangle"
            :shape="iKeyIconShape.SQUARE"
            :type="iKeyIconType.ERROR"
            :icon-style="iKeyIconStyle.SOLID"
            :size="iKeyIconSize.MEDIUM"
          ></i-key-icon>
          <div class="text d-flex flex-column i-gap-1 align-items-center">
            <div class="title i-text-button-2 i-text-neutral-120">
              Failed to load Report
            </div>
            <div class="subtitle i-text-body-2 i-text-neutral-110">
              The AI Compliance Analyst failed to generate a report
            </div>
          </div>
        </div>

        <!-- finished state -->
        <div v-else-if="complianceRunIsFinished" class="content">
          <div class="report-items-section d-flex flex-column i-py-11 i-gap-9">
            <!-- Overview Section -->
            <div
              v-if="Object.keys(profileOverviewObj).length"
              class="title i-text-heading-4"
            >
              Overview
            </div>
            <customer-compliance-report-item
              v-for="(value, key) in profileOverviewObj"
              :key="key"
              :title="formatFieldName(key)"
              :text="value"
            ></customer-compliance-report-item>
          </div>

          <!-- Recommendation Section -->
          <div
            v-if="latestComplianceRun?.output?.analysis"
            class="report-items-section d-flex flex-column i-py-11 i-gap-9"
          >
            <customer-compliance-report-item
              title="Recommendation"
              :title-paragraph="true"
              :text="latestComplianceRun.output?.analysis"
            ></customer-compliance-report-item>
          </div>

          <!-- Company Checks Section -->
          <div
            v-if="companyChecks.length"
            class="report-items-section d-flex flex-column i-py-11 i-gap-9"
          >
            <div class="title i-text-heading-4">Company Checks</div>
            <customer-compliance-report-item
              v-for="(check, index) in companyChecks"
              :key="'company-check-' + index"
              :title="check.title"
              :text="check.rationale"
            ></customer-compliance-report-item>
          </div>

          <!-- Beneficial Owner Checks Section -->
          <div
            v-if="beneficialOwnerNames.length"
            class="report-items-section d-flex flex-column i-py-11 i-gap-9"
          >
            <div class="title i-text-heading-4">UBO Checks</div>
            <div class="ubo-items-list">
              <div
                v-for="(owner, ownerIndex) in beneficialOwnerNames"
                :key="'owner-' + ownerIndex"
                class="d-flex flex-column"
              >
                <div class="i-text-heading-5 i-text-neutral-120 i-mb-6">
                  {{ owner }}
                </div>
                <div class="check-list d-flex flex-column i-gap-9">
                  <customer-compliance-report-item
                    v-for="(check, index) in getChecksForBeneficialOwner(owner)"
                    :key="'ubo-check-' + ownerIndex + '-' + index"
                    :title="check.title"
                    :text="check.rationale"
                  ></customer-compliance-report-item>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.customer-compliance-report {
  position: relative;
  overflow-y: auto;

  .customer-compliance-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .report-items-section {
    border-bottom: 1px solid $light-neutral-60;
  }

  .empty-state {
    border: 1px solid $light-neutral-60;
  }

  .back-button {
    position: absolute;
    top: 20px;
    left: 32px;
  }
}
</style>
